import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import {
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Copy,
  ToggleLeft,
  ToggleRight,
  FolderOpen,
  FileText,
  Video,
  Upload
} from 'lucide-react';
import uploadToR2 from './utils/r2Upload';

const Container = styled.div`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 1.5rem 2rem;
  margin-top: 1rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  position: relative;
  z-index: 1;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #fff;
  margin: 0;
`;

const Tabs = styled.div`
  display: flex;
  gap: 0.5rem;
  background: rgba(0, 0, 0, 0.2);
  padding: 0.25rem;
  border-radius: 8px;
`;

const Tab = styled.button`
  padding: 0.5rem 1.25rem;
  border: none;
  background: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &.active {
    background: rgba(102, 126, 234, 0.3);
    color: #fff;
  }

  &:hover {
    background: rgba(102, 126, 234, 0.2);
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem;
  margin-bottom: 1.5rem;
  justify-content: space-between;
`;

const ToolbarLeft = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  flex: 1;
  min-width: 0;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 160px;
  flex: 1;
  max-width: 280px;
`;

const Label = styled.label`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.65);
`;

const Input = styled.input`
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.2);
  color: #fff;
  font-size: 0.9rem;
  width: 100%;
  box-sizing: border-box;

  &::placeholder {
    color: rgba(255, 255, 255, 0.45);
  }
  &:focus {
    outline: none;
    border-color: rgba(102, 126, 234, 0.8);
  }
`;

const Select = styled.select`
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.25);
  color: #fff;
  font-size: 0.9rem;
  min-width: 140px;

  option {
    color: #1a202c;
  }
  &:focus {
    outline: none;
    border-color: rgba(102, 126, 234, 0.8);
  }
`;

const Button = styled.button`
  padding: 0.65rem 1.25rem;
  border-radius: 8px;
  border: none;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &.primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: #fff;
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    }
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    &:hover {
      background: rgba(255, 255, 255, 0.15);
    }
  }

  &.danger {
    background: rgba(239, 68, 68, 0.2);
    color: #ef4444;
    &:hover {
      background: rgba(239, 68, 68, 0.3);
    }
  }

  &.icon-only {
    padding: 0.5rem;
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
`;

const TableHeader = styled.th`
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  white-space: nowrap;
`;

const TableCell = styled.td`
  padding: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.9);
  vertical-align: middle;
`;

const TableRow = styled.tr`
  &:hover {
    background: rgba(255, 255, 255, 0.03);
  }
`;

const Badge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;

  &.active {
    background: rgba(34, 197, 94, 0.2);
    color: #22c55e;
  }

  &.inactive {
    background: rgba(239, 68, 68, 0.2);
    color: #ef4444;
  }

  &.featured {
    background: rgba(251, 191, 36, 0.2);
    color: #fbbf24;
  }
`;

const Modal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
`;

const ModalContent = styled(motion.div)`
  background: rgba(30, 30, 40, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  padding: 2rem;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #fff;
  margin: 0;
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const TextArea = styled.textarea`
  padding: 0.65rem 0.85rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.2);
  color: #fff;
  font-size: 0.9rem;
  width: 100%;
  box-sizing: border-box;
  min-height: 100px;
  resize: vertical;

  &::placeholder {
    color: rgba(255, 255, 255, 0.45);
  }
  &:focus {
    outline: none;
    border-color: rgba(102, 126, 234, 0.8);
  }
`;

const FileUploadArea = styled.div`
  border: 2px dashed rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: rgba(0, 0, 0, 0.2);

  &:hover {
    border-color: rgba(102, 126, 234, 0.6);
    background: rgba(102, 126, 234, 0.1);
  }
`;

const FileUploadLabel = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.8);
`;

const FileUploadText = styled.span`
  font-size: 0.95rem;
  font-weight: 500;
`;

const FileUploadSubtext = styled.span`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.5);
`;

const FileUrlDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: rgba(102, 126, 234, 0.1);
  border-radius: 6px;
  border: 1px solid rgba(102, 126, 234, 0.3);
`;

const FileUrlText = styled.span`
  flex: 1;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.8);
  word-break: break-all;
`;

const RemoveFileButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(239, 68, 68, 0.2);
    color: #ef4444;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: rgba(255, 255, 255, 0.5);
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem;
  color: rgba(255, 255, 255, 0.7);
`;

const LearningResourcesManagement = ({ token }) => {
  const [activeTab, setActiveTab] = useState('categories');
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [modalType, setModalType] = useState('category');
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadedFileUrl, setUploadedFileUrl] = useState('');

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterContentType, setFilterContentType] = useState('all');

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/v1/learning/categories/admin?isActive=${filterActive === 'all' ? '' : filterActive}&search=${searchTerm}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setCategories(data.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  }, [token, filterActive, searchTerm, API_BASE]);

  const fetchSubcategories = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/learning/subcategories/admin`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setSubcategories(data.data);
      }
    } catch (error) {
      console.error('Error fetching subcategories:', error);
    }
  }, [token, API_BASE]);

  const fetchResources = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterActive !== 'all') params.append('isActive', filterActive);
      if (filterCategory !== 'all') params.append('categoryId', filterCategory);
      if (filterContentType !== 'all') params.append('contentType', filterContentType);

      const response = await fetch(`${API_BASE}/api/v1/learning/resources/admin?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setResources(data.data);
      }
    } catch (error) {
      console.error('Error fetching resources:', error);
    } finally {
      setLoading(false);
    }
  }, [token, searchTerm, filterActive, filterCategory, filterContentType, API_BASE]);

  useEffect(() => {
    if (activeTab === 'categories') {
      fetchCategories();
      fetchSubcategories();
    } else {
      fetchResources();
    }
  }, [activeTab, fetchCategories, fetchSubcategories, fetchResources]);

  const handleCreate = (type) => {
    setModalType(type);
    setModalMode('create');
    setEditingItem(null);
    setFormData({});
    setUploadedFileUrl('');
    setModalOpen(true);
  };

  const handleEdit = (item, type) => {
    setModalType(type);
    setModalMode('edit');
    setEditingItem(item);
    setFormData(item);
    setUploadedFileUrl(item.file_url || '');
    setModalOpen(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingFile(true);
      const resourceType = formData.resource_type || 'PDF';
      const url = await uploadToR2(file, resourceType);
      setUploadedFileUrl(url);
      setFormData({ ...formData, file_url: url });
    } catch (error) {
      alert(`Upload failed: ${error.message}`);
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      const endpoint = type === 'category' 
        ? `${API_BASE}/api/v1/learning/categories/${id}/soft-delete`
        : type === 'subcategory'
        ? `${API_BASE}/api/v1/learning/subcategories/${id}/soft-delete`
        : `${API_BASE}/api/v1/learning/resources/${id}/soft-delete`;

      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        if (activeTab === 'categories') {
          fetchCategories();
          fetchSubcategories();
        } else {
          fetchResources();
        }
      }
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const handleToggleActive = async (id, isActive, type) => {
    try {
      const endpoint = type === 'category'
        ? `${API_BASE}/api/v1/learning/categories/${id}/toggle-active`
        : type === 'subcategory'
        ? `${API_BASE}/api/v1/learning/subcategories/${id}/toggle-active`
        : `${API_BASE}/api/v1/learning/resources/${id}/toggle-active`;

      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !isActive })
      });

      if (response.ok) {
        if (activeTab === 'categories') {
          fetchCategories();
          fetchSubcategories();
        } else {
          fetchResources();
        }
      }
    } catch (error) {
      console.error('Error toggling active:', error);
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/learning/resources/${id}/duplicate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        fetchResources();
      }
    } catch (error) {
      console.error('Error duplicating resource:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const endpoint = modalType === 'category'
        ? `${API_BASE}/api/v1/learning/categories${modalMode === 'edit' ? `/${editingItem.id}` : ''}`
        : modalType === 'subcategory'
        ? `${API_BASE}/api/v1/learning/subcategories${modalMode === 'edit' ? `/${editingItem.id}` : ''}`
        : `${API_BASE}/api/v1/learning/resources${modalMode === 'edit' ? `/${editingItem.id}` : ''}`;

      const method = modalMode === 'create' ? 'POST' : 'PUT';

      const response = await fetch(endpoint, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setModalOpen(false);
        if (activeTab === 'categories') {
          fetchCategories();
          fetchSubcategories();
        } else {
          fetchResources();
        }
      }
    } catch (error) {
      console.error('Error saving item:', error);
    }
  };

  const handleAddSubcategory = (categoryId) => {
    setModalType('subcategory');
    setModalMode('create');
    setEditingItem(null);
    setFormData({ category_id: categoryId });
    setModalOpen(true);
  };

  const getContentTypeIcon = (contentType) => {
    switch (contentType?.toLowerCase()) {
      case 'html':
        return <FileText size={16} />;
      case 'video':
      case 'mp4':
        return <Video size={16} />;
      case 'pdf':
      case 'docx':
      case 'ppt':
      case 'pptx':
        return <FileText size={16} />;
      default:
        return <FileText size={16} />;
    }
  };

  const filteredCategories = useMemo(() => {
    return categories.filter(cat => {
      if (filterActive === 'all') return true;
      return cat.is_active === (filterActive === 'true');
    });
  }, [categories, filterActive]);

  const filteredResources = useMemo(() => {
    return resources.filter(res => {
      if (filterActive !== 'all' && res.is_active !== (filterActive === 'true')) return false;
      if (filterCategory !== 'all' && res.category_id !== filterCategory) return false;
      if (filterContentType !== 'all' && res.content_type !== filterContentType) return false;
      return true;
    });
  }, [resources, filterActive, filterCategory, filterContentType]);

  return (
    <>
      <Container>
        <Header>
          <Title>Learning Resources</Title>
          <Tabs>
            <Tab 
              className={activeTab === 'categories' ? 'active' : ''} 
              onClick={() => setActiveTab('categories')}
            >
              Categories
            </Tab>
            <Tab 
              className={activeTab === 'resources' ? 'active' : ''} 
              onClick={() => setActiveTab('resources')}
            >
              Resources
            </Tab>
          </Tabs>
        </Header>

        {activeTab === 'categories' && (
        <>
          <Toolbar>
            <ToolbarLeft>
              <Field>
                <Label>Search</Label>
                <Input
                  placeholder="Search categories..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Field>
              <Field>
                <Label>Status</Label>
                <Select value={filterActive} onChange={(e) => setFilterActive(e.target.value)}>
                  <option value="all">All</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </Select>
              </Field>
            </ToolbarLeft>
            <Button className="primary" onClick={() => handleCreate('category')}>
              <Plus size={18} />
              Add Category
            </Button>
          </Toolbar>

          {loading ? (
            <LoadingState>Loading categories...</LoadingState>
          ) : filteredCategories.length === 0 ? (
            <EmptyState>No categories found</EmptyState>
          ) : (
            <TableContainer>
              <Table>
                <thead>
                  <tr>
                    <TableHeader>Name</TableHeader>
                    <TableHeader>Slug</TableHeader>
                    <TableHeader>Subcategories</TableHeader>
                    <TableHeader>Order</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Actions</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {filteredCategories.map((category) => {
                    const categorySubcategories = subcategories.filter(sub => sub.category_id === category.id);
                    return (
                      <React.Fragment key={category.id}>
                        <TableRow>
                          <TableCell>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              {category.icon && <span>{category.icon}</span>}
                              {category.name}
                            </div>
                          </TableCell>
                          <TableCell>{category.slug}</TableCell>
                          <TableCell>{categorySubcategories.length}</TableCell>
                          <TableCell>{category.display_order}</TableCell>
                          <TableCell>
                            <Badge className={category.is_active ? 'active' : 'inactive'}>
                              {category.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <Button className="icon-only secondary" onClick={() => handleEdit(category, 'category')}>
                                <Edit size={16} />
                              </Button>
                              <Button className="icon-only secondary" onClick={() => handleAddSubcategory(category.id)}>
                                <Plus size={16} />
                              </Button>
                              <Button className="icon-only secondary" onClick={() => handleToggleActive(category.id, category.is_active, 'category')}>
                                {category.is_active ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                              </Button>
                              <Button className="icon-only danger" onClick={() => handleDelete(category.id, 'category')}>
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                        {categorySubcategories.map(sub => (
                          <TableRow key={sub.id}>
                            <TableCell style={{ paddingLeft: '2rem' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <FolderOpen size={14} />
                                {sub.name}
                              </div>
                            </TableCell>
                            <TableCell style={{ paddingLeft: '2rem' }}>{sub.slug}</TableCell>
                            <TableCell>-</TableCell>
                            <TableCell>{sub.display_order}</TableCell>
                            <TableCell>
                              <Badge className={sub.is_active ? 'active' : 'inactive'}>
                                {sub.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <Button className="icon-only secondary" onClick={() => handleEdit(sub, 'subcategory')}>
                                  <Edit size={16} />
                                </Button>
                                <Button className="icon-only secondary" onClick={() => handleToggleActive(sub.id, sub.is_active, 'subcategory')}>
                                  {sub.is_active ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                                </Button>
                                <Button className="icon-only danger" onClick={() => handleDelete(sub.id, 'subcategory')}>
                                  <Trash2 size={16} />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {activeTab === 'resources' && (
        <>
          <Toolbar>
            <ToolbarLeft>
              <Field>
                <Label>Search</Label>
                <Input
                  placeholder="Search resources..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Field>
              <Field>
                <Label>Category</Label>
                <Select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                  <option value="all">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </Select>
              </Field>
              <Field>
                <Label>Content Type</Label>
                <Select value={filterContentType} onChange={(e) => setFilterContentType(e.target.value)}>
                  <option value="all">All Types</option>
                  <option value="HTML">HTML</option>
                  <option value="PDF">PDF</option>
                  <option value="DOCX">DOCX</option>
                  <option value="PPT">PPT</option>
                  <option value="PPTX">PPTX</option>
                  <option value="VIDEO">VIDEO</option>
                </Select>
              </Field>
              <Field>
                <Label>Status</Label>
                <Select value={filterActive} onChange={(e) => setFilterActive(e.target.value)}>
                  <option value="all">All</option>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </Select>
              </Field>
            </ToolbarLeft>
            <Button className="primary" onClick={() => handleCreate('resource')}>
              <Plus size={18} />
              Add Resource
            </Button>
          </Toolbar>

          {loading ? (
            <LoadingState>Loading resources...</LoadingState>
          ) : filteredResources.length === 0 ? (
            <EmptyState>No resources found</EmptyState>
          ) : (
            <TableContainer>
              <Table>
                <thead>
                  <tr>
                    <TableHeader>Title</TableHeader>
                    <TableHeader>Category</TableHeader>
                    <TableHeader>Type</TableHeader>
                    <TableHeader>Featured</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Actions</TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {filteredResources.map((resource) => (
                    <TableRow key={resource.id}>
                      <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {getContentTypeIcon(resource.content_type)}
                          {resource.title}
                        </div>
                      </TableCell>
                      <TableCell>{resource.category_name || '-'}</TableCell>
                      <TableCell>
                        <Badge>{resource.content_type}</Badge>
                      </TableCell>
                      <TableCell>
                        {resource.is_featured && <Badge className="featured">Featured</Badge>}
                      </TableCell>
                      <TableCell>
                        <Badge className={resource.is_active ? 'active' : 'inactive'}>
                          {resource.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <Button className="icon-only secondary" onClick={() => handleEdit(resource, 'resource')}>
                            <Edit size={16} />
                          </Button>
                          <Button className="icon-only secondary" onClick={() => handleDuplicate(resource.id)}>
                            <Copy size={16} />
                          </Button>
                          <Button className="icon-only secondary" onClick={() => handleToggleActive(resource.id, resource.is_active, 'resource')}>
                            {resource.is_active ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                          </Button>
                          <Button className="icon-only danger" onClick={() => handleDelete(resource.id, 'resource')}>
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      </Container>
      
      {modalOpen && createPortal(
        <AnimatePresence>
          <Modal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setModalOpen(false)}
          >
            <ModalContent
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle>
                  {modalMode === 'create' ? 'Create' : 'Edit'} {modalType}
                </ModalTitle>
                <Button className="icon-only secondary" onClick={() => setModalOpen(false)}>
                  <X size={20} />
                </Button>
              </ModalHeader>
              <form onSubmit={handleSubmit}>
                <ModalBody>
                  {modalType === 'category' && (
                    <>
                      <Field>
                        <Label>Name *</Label>
                        <Input
                          required
                          value={formData.name || ''}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                      </Field>
                      <Field>
                        <Label>Slug *</Label>
                        <Input
                          required
                          value={formData.slug || ''}
                          onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                        />
                      </Field>
                      <Field>
                        <Label>Description</Label>
                        <TextArea
                          value={formData.description || ''}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        />
                      </Field>
                      <Field>
                        <Label>Icon</Label>
                        <Input
                          value={formData.icon || ''}
                          onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                          placeholder="Emoji or icon name"
                        />
                      </Field>
                      <Field>
                        <Label>Display Order</Label>
                        <Input
                          type="number"
                          value={formData.display_order || 0}
                          onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                        />
                      </Field>
                    </>
                  )}

                  {modalType === 'subcategory' && (
                    <>
                      <Field>
                        <Label>Category *</Label>
                        <Select
                          required
                          value={formData.category_id || ''}
                          onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                        >
                          <option value="">Select Category</option>
                          {categories.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                          ))}
                        </Select>
                      </Field>
                      <Field>
                        <Label>Name *</Label>
                        <Input
                          required
                          value={formData.name || ''}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                      </Field>
                      <Field>
                        <Label>Slug *</Label>
                        <Input
                          required
                          value={formData.slug || ''}
                          onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                        />
                      </Field>
                      <Field>
                        <Label>Description</Label>
                        <TextArea
                          value={formData.description || ''}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        />
                      </Field>
                      <Field>
                        <Label>Display Order</Label>
                        <Input
                          type="number"
                          value={formData.display_order || 0}
                          onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                        />
                      </Field>
                    </>
                  )}

                  {modalType === 'resource' && (
                    <>
                      <Field>
                        <Label>Category *</Label>
                        <Select
                          required
                          value={formData.category_id || ''}
                          onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                        >
                          <option value="">Select Category</option>
                          {categories.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                          ))}
                        </Select>
                      </Field>
                      <Field>
                        <Label>Subcategory</Label>
                        <Select
                          value={formData.subcategory_id || ''}
                          onChange={(e) => setFormData({ ...formData, subcategory_id: e.target.value })}
                        >
                          <option value="">No Subcategory</option>
                          {subcategories
                            .filter(sub => sub.category_id === formData.category_id)
                            .map(sub => (
                              <option key={sub.id} value={sub.id}>{sub.name}</option>
                            ))}
                        </Select>
                      </Field>
                      <Field>
                        <Label>Title *</Label>
                        <Input
                          required
                          value={formData.title || ''}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        />
                      </Field>
                      <Field>
                        <Label>Slug *</Label>
                        <Input
                          required
                          value={formData.slug || ''}
                          onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                        />
                      </Field>
                      <Field>
                        <Label>Short Description</Label>
                        <TextArea
                          value={formData.short_description || ''}
                          onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                        />
                      </Field>
                      <Field>
                        <Label>Resource Type *</Label>
                        <Select
                          required
                          value={formData.resource_type || ''}
                          onChange={(e) => {
                            const newResourceType = e.target.value;
                            setFormData({ 
                              ...formData, 
                              resource_type: newResourceType,
                              content_type: newResourceType // Auto-sync content_type with resource_type
                            });
                          }}
                        >
                          <option value="">Select Type</option>
                          <option value="HTML">HTML</option>
                          <option value="PDF">PDF</option>
                          <option value="VIDEO">VIDEO</option>
                          <option value="IMAGE">IMAGE</option>
                        </Select>
                      </Field>
                      
                      {/* Conditional content input based on resource type */}
                      {formData.resource_type === 'HTML' ? (
                        <Field>
                          <Label>HTML Content *</Label>
                          <TextArea
                            required
                            value={formData.content_html || ''}
                            onChange={(e) => setFormData({ ...formData, content_html: e.target.value })}
                            placeholder="Enter HTML content for the resource"
                            rows={10}
                          />
                        </Field>
                      ) : (
                        <Field>
                          <Label>Upload File *</Label>
                          <FileUploadArea>
                            <input
                              type="file"
                              id="resourceFile"
                              onChange={handleFileUpload}
                              accept={
                                formData.resource_type === 'PDF' ? '.pdf' :
                                formData.resource_type === 'VIDEO' ? 'video/*' :
                                formData.resource_type === 'IMAGE' ? 'image/*' :
                                '*'
                              }
                              style={{ display: 'none' }}
                            />
                            <FileUploadLabel htmlFor="resourceFile">
                              {uploadingFile ? (
                                <>
                                  <Upload size={24} />
                                  <FileUploadText>Uploading...</FileUploadText>
                                </>
                              ) : uploadedFileUrl ? (
                                <>
                                  <FileText size={24} />
                                  <FileUploadText>File Uploaded</FileUploadText>
                                  <FileUploadSubtext>{uploadedFileUrl.substring(uploadedFileUrl.lastIndexOf('/') + 1)}</FileUploadSubtext>
                                </>
                              ) : (
                                <>
                                  <Upload size={24} />
                                  <FileUploadText>Click to upload {formData.resource_type || 'file'}</FileUploadText>
                                  <FileUploadSubtext>Max size: 50MB</FileUploadSubtext>
                                </>
                              )}
                            </FileUploadLabel>
                          </FileUploadArea>
                          {uploadedFileUrl && (
                            <FileUrlDisplay>
                              <FileUrlText>{uploadedFileUrl}</FileUrlText>
                              <RemoveFileButton onClick={() => {
                                setUploadedFileUrl('');
                                setFormData({ ...formData, file_url: '' });
                              }}>
                                <X size={16} />
                              </RemoveFileButton>
                            </FileUrlDisplay>
                          )}
                        </Field>
                      )}
                      <Field>
                        <Label>Thumbnail URL</Label>
                        <Input
                          value={formData.thumbnail || ''}
                          onChange={(e) => setFormData({ ...formData, thumbnail: e.target.value })}
                          placeholder="URL to the thumbnail image"
                        />
                      </Field>
                      <Field>
                        <Label>Estimated Read Time (minutes)</Label>
                        <Input
                          type="number"
                          value={formData.estimated_read_time || ''}
                          onChange={(e) => setFormData({ ...formData, estimated_read_time: parseInt(e.target.value) || null })}
                        />
                      </Field>
                      <Field>
                        <Label>Display Order</Label>
                        <Input
                          type="number"
                          value={formData.display_order || 0}
                          onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                        />
                      </Field>
                      <Field>
                        <Label>Featured</Label>
                        <Select
                          value={formData.is_featured ? 'true' : 'false'}
                          onChange={(e) => setFormData({ ...formData, is_featured: e.target.value === 'true' })}
                        >
                          <option value="false">No</option>
                          <option value="true">Yes</option>
                        </Select>
                      </Field>
                    </>
                  )}
                </ModalBody>
                <ModalFooter>
                  <Button className="secondary" onClick={() => setModalOpen(false)}>
                    Cancel
                  </Button>
                  <Button className="primary" type="submit">
                    <Save size={16} />
                    {modalMode === 'create' ? 'Create' : 'Update'}
                  </Button>
                </ModalFooter>
              </form>
            </ModalContent>
          </Modal>
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default LearningResourcesManagement;
